import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { searchForFilter } from "@mirage/service-search";
import { useCheckIsMounted } from "@mirage/shared/hooks/useIsMounted";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { contentTypeToSearchFilter } from "@mirage/shared/search/search-filters";
import { MultimediaView } from "@mirage/shared/search/search-result";
import { useEffect, useState } from "react";
import { useMultimediaView } from "../Multimedia/hooks/useMultimediaView";
import { imagesCategoryConfig } from "./config";

import type { SearchResult } from "@mirage/service-dbx-api";

const imageFilter = contentTypeToSearchFilter(imagesCategoryConfig.contentType);

const logger = tagged("category-search");

export function useFilterSearch(
  query: string,
  filter: SearchFilter | null,
  limit: number | null,
  shouldSkipCache?: boolean,
) {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<SearchResult[]>([]);
  const checkIsMounted = useCheckIsMounted();
  const isMessagesEnabled =
    useFeatureFlagValue("dash_2025_03_03_search_message_results") === "ON";

  const multimediaView = useMultimediaView(imageFilter ? [imageFilter] : []);

  useEffect(() => {
    if (!query || !filter) return;

    setLoading(true);

    const fetchResults = async () => {
      try {
        const res = await searchForFilter(
          query,
          filter,
          limit,
          isMessagesEnabled,
          multimediaView !== MultimediaView.OFF,
          shouldSkipCache,
        );
        if (!checkIsMounted()) return;
        setResults(res);
      } catch (error) {
        logger.error(
          `Error fetching category search results for ${filter.id}`,
          error,
        );
      } finally {
        if (checkIsMounted()) setLoading(false);
      }
    };

    fetchResults();
  }, [
    query,
    filter,
    checkIsMounted,
    filter?.id,
    isMessagesEnabled,
    limit,
    multimediaView,
  ]);

  return {
    loading,
    results,
  };
}
